// Storage helpers for Lovable Cloud buckets.
// Buckets are private, so uploads return a long-lived signed URL that is stored
// alongside the record (avatar_url, media_items, ...).

import { supabase } from '@/integrations/supabase/client';

export type MediaBucket =
  | 'avatars'
  | 'post-media'
  | 'highlight-media'
  | 'message-media'
  | 'offer-media'
  | 'application-docs'
  | 'report-media';

const SIGNED_URL_TTL = 60 * 60 * 24 * 365; // 1 year

const EXT_BY_MIME: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/gif': 'gif',
  'video/mp4': 'mp4',
  'video/quicktime': 'mov',
  'video/webm': 'webm',
};

export function isDataUrl(value?: string | null): boolean {
  return !!value && value.startsWith('data:');
}

function dataUrlToBlob(dataUrl: string): { blob: Blob; mime: string } {
  const [header, base64] = dataUrl.split(',');
  const mime = header.match(/data:([^;]+)/)?.[1] ?? 'application/octet-stream';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return { blob: new Blob([bytes], { type: mime }), mime };
}

async function signed(bucket: MediaBucket, path: string): Promise<string | null> {
  const { data, error } = await supabase.storage.from(bucket).createSignedUrl(path, SIGNED_URL_TTL);
  if (error) {
    console.warn('[storage] signing failed', error);
    return null;
  }
  return data?.signedUrl ?? null;
}

/** Upload a Blob/File under `${userId}/...` and return a signed URL. */
export async function uploadMedia(
  bucket: MediaBucket,
  userId: string,
  file: Blob,
  mime?: string,
): Promise<string | null> {
  const type = mime || file.type || 'application/octet-stream';
  const ext = EXT_BY_MIME[type] ?? 'bin';
  const path = `${userId}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
  const { error } = await supabase.storage.from(bucket).upload(path, file, {
    contentType: type,
    upsert: false,
  });
  if (error) {
    console.warn('[storage] upload failed', error);
    return null;
  }
  return signed(bucket, path);
}

/**
 * Upload a data-URL (produced by the croppers / media pickers) and return a
 * signed URL. Non data-URL values are returned untouched.
 */
export async function uploadDataUrl(
  bucket: MediaBucket,
  userId: string,
  value?: string | null,
): Promise<string | undefined> {
  if (!value) return undefined;
  if (!isDataUrl(value)) return value;
  const { blob, mime } = dataUrlToBlob(value);
  const url = await uploadMedia(bucket, userId, blob, mime);
  return url ?? undefined;
}

// --------------------------------------------------------------- documents

/** Allowed application document formats (CV, cover letter, ...). */
export const DOCUMENT_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/jpeg',
  'image/png',
] as const;

export const MAX_DOCUMENT_BYTES = 5 * 1024 * 1024; // 5 MB
export const MAX_IMAGE_BYTES = 8 * 1024 * 1024; // 8 MB

const DOC_EXT_BY_MIME: Record<string, string> = {
  'application/pdf': 'pdf',
  'application/msword': 'doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'image/jpeg': 'jpg',
  'image/png': 'png',
};

export type UploadResult = { url?: string; error?: string };

/** Upload an application document (CV) to the private `application-docs` bucket. */
export async function uploadApplicationDocument(userId: string, file: File): Promise<UploadResult> {
  if (!(DOCUMENT_MIME_TYPES as readonly string[]).includes(file.type)) {
    return { error: 'Unsupported file format' };
  }
  if (file.size > MAX_DOCUMENT_BYTES) {
    return { error: 'File is larger than 5 MB' };
  }
  const ext = DOC_EXT_BY_MIME[file.type] ?? 'bin';
  const path = `${userId}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
  const { error } = await supabase.storage.from('application-docs').upload(path, file, {
    contentType: file.type,
    upsert: false,
  });
  if (error) return { error: error.message };
  const url = await signed('application-docs', path);
  return url ? { url } : { error: 'Could not create a signed URL' };
}

/** Upload an offer cover image to the private `offer-media` bucket. */
export async function uploadOfferImage(userId: string, file: File): Promise<UploadResult> {
  if (!file.type.startsWith('image/')) return { error: 'Unsupported file format' };
  if (file.size > MAX_IMAGE_BYTES) return { error: 'Image is larger than 8 MB' };
  const url = await uploadMedia('offer-media', userId, file, file.type);
  return url ? { url } : { error: 'Upload failed' };
}
